import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { MessageProvider } from './contexts/MessageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Register from './pages/Register/Register';
import { useAuth } from './contexts/AuthContext';
import Quiz from './pages/Quiz/Quiz';
import Results from './pages/Results/Results';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import ProgrammingLessonCreator from './pages/ProgrammingLessonCreator/ProgrammingLessonCreator';
import ProgrammingLesson from './pages/ProgrammingLesson/ProgrammingLesson';
import LessonResults from './pages/LessonResults/LessonResults';
import Join from './pages/Join/Join';
import LessonEditor from './pages/LessonEditor/LessonEditor';
import ProgrammingLessonEditor from './pages/ProgrammingLessonEditor/ProgrammingLessonEditor';
import ClassProgress from './pages/ClassProgress/ClassProgress';
import StudentProgress from './pages/StudentProgress/StudentProgress';
import InteractiveLesson from './pages/InteractiveLesson/InteractiveLesson';
import InteractiveLessonCreator from './pages/InteractiveLessonCreator/InteractiveLessonCreator';
import InteractiveLessonEditor from './pages/InteractiveLessonEditor/InteractiveLessonEditor';
import QuizLessonCreator from './pages/QuizLessonCreator/QuizLessonCreator';
import CompetenceAims from './pages/CompetenceAims/CompetenceAims';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Laster...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Laster...</div>;
  }
  
  if (user) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// App Component
const AppContent = () => {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/join" 
        element={
          <PublicRoute>
            <Join />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      
      {/* Public Quiz Routes */}
      <Route path="/quiz/lessons/:id" element={<Quiz />} />
      <Route path="/results/:resultId?" element={<Results />} />
      
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/quiz-lessons/create" 
        element={
          <ProtectedRoute>
            <QuizLessonCreator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programming-lessons/create" 
        element={
          <ProtectedRoute>
            <ProgrammingLessonCreator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programming-lessons/:id" 
        element={<ProgrammingLesson />} 
      />
      <Route 
        path="/lessons/:lessonId/results" 
        element={
          <ProtectedRoute>
            <LessonResults />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/lessons/:id/edit" 
        element={
          <ProtectedRoute>
            <LessonEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programming-lessons/:id/edit" 
        element={
          <ProtectedRoute>
            <ProgrammingLessonEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes/:classId/progress" 
        element={
          <ProtectedRoute>
            <ClassProgress />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute>
            <ClassProgress />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes/progress" 
        element={
          <ProtectedRoute>
            <ClassProgress />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students/:studentId/progress" 
        element={
          <ProtectedRoute>
            <StudentProgress />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interactive-lessons/create" 
        element={
          <ProtectedRoute>
            <InteractiveLessonCreator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interactive-lessons/:id" 
        element={
          <ProtectedRoute>
            <InteractiveLesson />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interactive-lessons/:id/edit" 
        element={
          <ProtectedRoute>
            <InteractiveLessonEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programming-lessons/:lessonId/results" 
        element={
          <ProtectedRoute>
            <LessonResults />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/competence-aims" 
        element={
          <ProtectedRoute>
            <CompetenceAims />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <MessageProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </MessageProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
